import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { User, Save, Pencil, X, Reply, Copy, Bot } from 'lucide-react';
import { useAuth } from './AuthModule';
import { API, AvatarFallback, normalizeHexTo6 } from './partials/profileHelpers';
import AvatarWithDesignerBadge from './partials/AvatarWithDesignerBadge';
import PublicProfileView from './partials/PublicProfileView';
import { useT } from '../i18n/I18nModule';

/** Stable key for mute list: prefer authorId, fallback authorName. */
function getAuthorKey(msg) {
  return msg.authorId != null ? String(msg.authorId) : (msg.authorName || '');
}

/**
 * Build display blocks: single message (own or other non-muted) or muted-group (consecutive same-author muted).
 * Ownership is determined by isOwn (from authorId === currentUser.id).
 */
function buildMessageBlocks(messages, mutedAuthorKeysSet) {
  if (!messages.length) return [];
  const muted = mutedAuthorKeysSet || new Set();
  const blocks = [];
  let i = 0;
  while (i < messages.length) {
    const msg = messages[i];
    const authorKey = getAuthorKey(msg);
    const isMuted = !!authorKey && muted.has(authorKey);
    if (msg.isOwn || !isMuted) {
      blocks.push({ type: 'single', msg });
      i += 1;
      continue;
    }
    const group = [msg];
    i += 1;
    while (i < messages.length) {
      const next = messages[i];
      if (next.isOwn || getAuthorKey(next) !== authorKey || !muted.has(getAuthorKey(next))) break;
      group.push(next);
      i += 1;
    }
    blocks.push({
      type: 'muted-group',
      authorKey,
      authorName: msg.authorName || '?',
      messages: group,
    });
  }
  return blocks;
}

/** Escape HTML to prevent XSS. */
function escapeHtml(s) {
  const str = String(s ?? '');
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Strip HTML to plain text for pasting into input.
 * - <a> elements are rendered as "-> {body}" (body = link text).
 * - /soi response: one line with space between parts, " | " only before "Kuuntele Audiuksessa".
 * - /sää and others: line breaks become " | ".
 */
function htmlToPlainText(html) {
  if (typeof html !== 'string') return '';
  let raw;
  if (typeof document === 'undefined') {
    const withLinkPrefix = html.replace(/<a\s[^>]*>([\s\S]*?)<\/a>/gi, (_, body) => {
      const inner = body.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
      return `-> ${inner}`;
    });
    raw = withLinkPrefix.replace(/<[^>]*>/g, '\n').replace(/<br\s*\/?>/gi, '\n').replace(/\r?\n/g, '\n').trim();
    raw = raw.split('\n').map((s) => s.replace(/\s+/g, ' ').trim()).join('\n');
  } else {
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('a').forEach((el) => {
      const body = (el.textContent || el.innerText || '').trim();
      el.textContent = body ? `-> ${body}` : '';
    });
    raw = (div.textContent || div.innerText || '').trim();
  }
  const isSoi = raw.includes('Nyt soi') && raw.includes('Kuuntele Audiuksessa');
  if (isSoi) {
    const oneLine = raw.replace(/\r?\n/g, ' ').replace(/\s+/g, ' ').trim();
    return oneLine.replace(/\s+-> Kuuntele Audiuksessa$/, ' | -> Kuuntele Audiuksessa');
  }
  return raw.replace(/\r?\n/g, ' | ').replace(/\s+/g, ' ').trim();
}

/** Format ISO date string for display (e.g. "17.2. 14:30"). */
function formatMessageTime(isoStr) {
  if (!isoStr || typeof isoStr !== 'string') return '—';
  try {
    const d = new Date(isoStr);
    if (Number.isNaN(d.getTime())) return '—';
    const day = d.getDate();
    const month = d.getMonth() + 1;
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${day}.${month}. ${h}:${m}`;
  } catch {
    return '—';
  }
}

/** Match @username (non-whitespace after @). Returns array of { type: 'text'|'mention', value, isYou? }. */
function parseMentions(text, currentUserDisplayName) {
  if (!text || typeof text !== 'string') return [{ type: 'text', value: text || '' }];
  const current = (currentUserDisplayName || '').trim().toLowerCase();
  const re = /@([^\s@]+)/g;
  const segments = [];
  let lastIndex = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, m.index) });
    }
    const name = m[1];
    const isYou = current && name.toLowerCase() === current;
    segments.push({ type: 'mention', value: `@${name}`, isYou });
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return segments.length ? segments : [{ type: 'text', value: text }];
}

const BOT_AUTHOR_LOWER = new Set(['pulina', 'pulinabotti']);

function isBotAuthorName(name, isCommandResponse) {
  const lower = (name || '').trim().toLowerCase();
  return BOT_AUTHOR_LOWER.has(lower) || !!isCommandResponse;
}

/** AI bot has no user profile — do not link the display name in chat bubbles. */
function isPulinabottiAuthorName(name) {
  return (name || '').trim().toLowerCase() === 'pulinabotti';
}

/** Fetch public profile snippets (avatar, badge) keyed by displayName (lowercase). */
function useChatAuthorVisuals(messages, currentUserDisplayName) {
  const [cache, setCache] = useState({});
  const cacheRef = useRef({});
  const pendingRef = useRef(new Set());

  const keysSig = useMemo(() => {
    const s = new Set();
    for (const m of messages || []) {
      const name = (m.authorName || '').trim();
      if (!name || isBotAuthorName(name, m.isCommandResponse)) continue;
      s.add(name);
    }
    const u = (currentUserDisplayName || '').trim();
    if (u && !isBotAuthorName(u, false)) s.add(u);
    return [...s].sort().join('\n');
  }, [messages, currentUserDisplayName]);

  useEffect(() => {
    const names = keysSig.split('\n').filter(Boolean);
    for (const name of names) {
      const key = name.toLowerCase();
      if (cacheRef.current[key] !== undefined || pendingRef.current.has(key)) continue;
      pendingRef.current.add(key);
      fetch(`${API}/api/profile/by-display-name/${encodeURIComponent(name)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          pendingRef.current.delete(key);
          if (!data) {
            cacheRef.current[key] = { error: true };
          } else {
            cacheRef.current[key] = {
              showAvatar: data.profile?.showAvatar ?? 'NO',
              avatarUrl: data.profile?.avatar,
              bubbleColor: data.bubbleColor,
              avatarDesigner: data.avatarDesigner ?? null,
            };
          }
          setCache({ ...cacheRef.current });
        })
        .catch(() => {
          pendingRef.current.delete(key);
          cacheRef.current[key] = { error: true };
          setCache({ ...cacheRef.current });
        });
    }
  }, [keysSig]);

  return cache;
}

function visualForAuthor(cache, authorName) {
  const key = (authorName || '').trim().toLowerCase();
  if (!key) return { error: true };
  const v = cache[key];
  if (v === undefined) return { loading: true };
  return v;
}

function ChatBubblePrefix({
  authorKey,
  authorName,
  isBot,
  isOwn,
  muted,
  muteCount,
  onToggleMute,
  visual,
  imageBroken,
  onImageError,
}) {
  const isMuted = muted && muteCount > 0;
  const title = isMuted ? `Poista mykistys: ${authorName}` : `Mykistä ${authorName}`;
  const label = isMuted ? `Poista mykistys: ${authorName}` : `Mykistä ${authorName}`;

  const face = () => {
    if (isBot) {
      return <Bot className="Chat-prefix-bot-icon" size={22} strokeWidth={2} aria-hidden />;
    }
    if (!visual || visual.loading) {
      return <span className="Chat-prefix-placeholder" aria-hidden />;
    }
    if (visual.error) {
      return <User className="Chat-prefix-fallback-icon" size={22} strokeWidth={2} aria-hidden />;
    }
    const showPhoto = visual.showAvatar === 'YES';
    if (!showPhoto) {
      return <User className="Chat-prefix-fallback-icon" size={22} strokeWidth={2} aria-hidden />;
    }
    if (visual.avatarUrl && !imageBroken) {
      return (
        <AvatarWithDesignerBadge designer={visual.avatarDesigner} variant="chat">
          <img
            src={visual.avatarUrl}
            alt=""
            className="Chat-prefix-avatar-img"
            onError={onImageError}
          />
        </AvatarWithDesignerBadge>
      );
    }
    return (
      <AvatarWithDesignerBadge designer={visual.avatarDesigner} variant="chat">
        <AvatarFallback
          bubbleColorHex={visual.bubbleColor}
          displayName={authorName}
          username={authorName}
          size="small"
          className="Chat-prefix-fallback"
        />
      </AvatarWithDesignerBadge>
    );
  };

  const wrapClass = `Chat-bubble-prefix${isOwn ? ' Chat-bubble-prefix--own' : ''}${
    isMuted ? ' Chat-bubble-prefix--muted-active' : ''
  }`;

  if (isOwn) {
    return <div className={wrapClass}>{face()}</div>;
  }

  return (
    <button
      type="button"
      className={wrapClass}
      onClick={() => onToggleMute && onToggleMute(authorKey)}
      title={title}
      aria-label={label}
    >
      {face()}
      {isMuted && (
        <span className="Chat-avatar-badge" aria-hidden="true">
          {muteCount}
        </span>
      )}
    </button>
  );
}

/**
 * Scrollable chat messages area. Renders message bubbles with edit/delete/reply actions.
 * Auto-scrolls to the latest message when the messages list updates.
 * Mentions (@username) are rendered bold and colored (light blue; darker blue if it's you).
 */

function ChatMessages({
  messages,
  editingMessageId,
  editingDraft,
  setEditingDraft,
  editInputRef,
  onStartEdit,
  onSaveEdit,
  onDeleteMessage,
  onReply,
  onEditInputKeyDown,
  currentUserDisplayName,
  mutedAuthorKeys = [],
  onToggleMuteAuthor,
  onInsertToInput,
  onDismissMessage,
  autoScroll = true,
  scrollToEndTrigger,
}) {
  const { bubbleColor, user } = useAuth();
  const t = useT();
  const botDisplayName = t('bot.senderName');
  const messagesEndRef = useRef(null);
  const [avatarImageBroken, setAvatarImageBroken] = useState({});
  const mutedSet = useMemo(() => new Set(mutedAuthorKeys), [mutedAuthorKeys]);
  const blocks = useMemo(() => buildMessageBlocks(messages, mutedSet), [messages, mutedSet]);
  const authorVisualCache = useChatAuthorVisuals(messages, currentUserDisplayName);
  const currentUserName = (user?.displayName || user?.username || '').trim().toLowerCase();
  const [publicProfileModalName, setPublicProfileModalName] = useState(null);

  const openPublicProfileFromChat = (rawName) => {
    const name = (rawName || '').trim();
    if (!name || name.toLowerCase() === currentUserName) return;
    if (isPulinabottiAuthorName(name)) return;
    if (BOT_AUTHOR_LOWER.has(name.toLowerCase())) return;
    setPublicProfileModalName(name);
  };

  const closePublicProfileModal = () => setPublicProfileModalName(null);

  useEffect(() => {
    if (!publicProfileModalName) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closePublicProfileModal();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [publicProfileModalName]);

  useEffect(() => {
    if (!publicProfileModalName) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [publicProfileModalName]);

  useEffect(() => {
    if (!autoScroll) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, autoScroll]);

  useEffect(() => {
    if (scrollToEndTrigger == null) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [scrollToEndTrigger]);

  return (
    <div className="Chat-messages">
      {blocks.map((block, idx) => {
        if (block.type === 'single') {
          const msg = block.msg;
          const isEditing = msg.isOwn && editingMessageId === msg.id;
          const textSegments = parseMentions(msg.text, currentUserDisplayName);
          const ownBgHex = msg.isOwn ? (normalizeHexTo6(bubbleColor) || '65c4a1') : null;
          const authorNameLower = (msg.authorName || '').trim().toLowerCase();
          const isPulinaBot =
            authorNameLower === 'pulina' ||
            authorNameLower === 'pulinabotti' ||
            msg.isCommandResponse;
          const authorKey = getAuthorKey(msg);
          const authorVisual = visualForAuthor(authorVisualCache, msg.authorName);
          const imgBroken = !!avatarImageBroken[authorKey];
          return (
            <div key={msg.id} className="Chat-bubble-row">
              <ChatBubblePrefix
                authorKey={authorKey}
                authorName={msg.authorName}
                isBot={isPulinaBot}
                isOwn={msg.isOwn}
                muted={false}
                muteCount={0}
                onToggleMute={onToggleMuteAuthor}
                visual={authorVisual}
                imageBroken={imgBroken}
                onImageError={() =>
                  setAvatarImageBroken((p) => ({ ...p, [authorKey]: true }))
                }
              />
              <div
                className={`Chat-bubble ${msg.isOwn ? 'Chat-bubble-own' : 'Chat-bubble-other'}${
                  isPulinaBot ? ' Chat-bubble-bot' : ''
                }`}
                style={ownBgHex ? { backgroundColor: `#${ownBgHex}` } : undefined}
              >
              {msg.isOwn ? (
                <span className="Chat-bubble-author">{msg.authorName}</span>
              ) : (
                <div className="Chat-bubble-author-row">
                  {msg.authorName ? (
                    isPulinabottiAuthorName(msg.authorName) ? (
                      <span className="Chat-bubble-author Chat-bubble-author--no-profile">
                        {botDisplayName}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="Chat-author-link"
                        onClick={() => openPublicProfileFromChat(msg.authorName)}
                      >
                        <span className="Chat-bubble-author">{msg.authorName}</span>
                      </button>
                    )
                  ) : (
                    <span className="Chat-bubble-author">{msg.authorName}</span>
                  )}
                </div>
              )}
              {msg.whisperTo ? (
                <span
                  className="Chat-bubble-text Chat-bubble-text--html Chat-bubble-whisper"
                  dangerouslySetInnerHTML={{
                    __html: msg.isOwn
                      ? (msg.whisperTo || '').trim().toLowerCase() === 'pulinabotti'
                        ? `Kuiskasit pulinabotille:<br />${escapeHtml(msg.text || '')}`
                        : `Kuiskasit ${escapeHtml((msg.whisperTo || '').trim())}:<br />${escapeHtml(msg.text || '')}`
                      : `${escapeHtml(
                          authorNameLower === 'pulinabotti' ? botDisplayName : (msg.authorName || '').trim(),
                        )} kuiskasi sinulle:<br />${
                          authorNameLower === 'pulinabotti' &&
                          (msg.htmlBody === true ||
                            (typeof msg.text === 'string' &&
                              msg.text.includes('Tervetuloa aulaan pulisemaan')))
                            ? msg.text || ''
                            : escapeHtml(msg.text || '')
                        }`,
                  }}
                />
              ) : isEditing ? (
                <textarea
                  ref={editInputRef}
                  className="Chat-bubble-edit-input"
                  value={editingDraft}
                  onChange={(e) => setEditingDraft(e.target.value)}
                  onKeyDown={onEditInputKeyDown}
                  maxLength={500}
                  rows={3}
                  aria-label="Muokkaa viestiä"
                />
              ) : msg.isCommandResponse ? (
                <div className="Chat-bubble-command-response">
                  <span
                    className="Chat-bubble-text Chat-bubble-text--html"
                    dangerouslySetInnerHTML={{ __html: msg.text }}
                  />
                  <span className="Chat-bubble-command-actions">
                    {msg.dismissible && typeof onDismissMessage === 'function' && (
                      <button
                        type="button"
                        className="Chat-bubble-dismiss"
                        onClick={() => onDismissMessage(msg.id)}
                        aria-label="Sulje"
                        title="Sulje"
                      >
                        <X size={18} strokeWidth={2} />
                      </button>
                    )}
                    {typeof onInsertToInput === 'function' && (
                      <button
                        type="button"
                        className="Chat-bubble-copy-to-input"
                        onClick={() => onInsertToInput(htmlToPlainText(msg.text))}
                        aria-label="Kopioi viestikenttään"
                        title="Kopioi viestikenttään"
                      >
                        <Copy size={18} strokeWidth={2} />
                      </button>
                    )}
                  </span>
                </div>
              ) : isPulinabottiAuthorName(msg.authorName) && msg.htmlBody === true ? (
                <span
                  className="Chat-bubble-text Chat-bubble-text--html"
                  dangerouslySetInnerHTML={{
                    __html:
                      (msg.replyTo && msg.replyTo.textPreview
                        ? `${escapeHtml(`→ ${msg.replyTo.textPreview}`)}<br/>`
                        : '') + (msg.text || ''),
                  }}
                />
              ) : (
                <span className="Chat-bubble-text">
                  {msg.replyTo && msg.replyTo.textPreview && (
                    <>
                      <span className="Chat-reply-to-label">
                        <b>→</b> {msg.replyTo.textPreview}
                      </span>
                      <br />
                    </>
                  )}
                  {textSegments.map((seg, i) => {
                    if (seg.type === 'text') return <span key={i}>{seg.value}</span>;
                    const mentionName = (seg.value || '').replace(/^@/, '').trim();
                    const mentionLower = mentionName.toLowerCase();
                    const mentionClickable =
                      mentionName &&
                      !seg.isYou &&
                      mentionLower !== currentUserName &&
                      !BOT_AUTHOR_LOWER.has(mentionLower);
                    if (mentionClickable) {
                      return (
                        <button
                          key={i}
                          type="button"
                          className="Chat-mention Chat-mention-link"
                          onClick={() => openPublicProfileFromChat(mentionName)}
                        >
                          {seg.value}
                        </button>
                      );
                    }
                    return (
                      <span
                        key={i}
                        className={`Chat-mention ${seg.isYou ? 'Chat-mention--you' : ''}`}
                      >
                        {seg.value}
                      </span>
                    );
                  })}
                </span>
              )}
              <div className="Chat-bubble-meta">
                {formatMessageTime(msg.createdAt)}
                {msg.updatedAt && (
                  <span className="Chat-bubble-edited"> (EDITED: {formatMessageTime(msg.updatedAt)})</span>
                )}
              </div>
              {msg.isOwn && !msg.isCommandResponse ? (
                <div className="Chat-own-actions">
                  {isEditing ? (
                    <button
                      type="button"
                      className="Chat-save"
                      onClick={onSaveEdit}
                      aria-label="Tallenna"
                      title="Tallenna (Ctrl+Enter)"
                    >
                      <Save className="Chat-action-icon" size={24} strokeWidth={2} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="Chat-edit"
                      onClick={() => onStartEdit(msg)}
                      aria-label="Muokkaa"
                      title="Muokkaa"
                    >
                      <Pencil className="Chat-action-icon" size={24} strokeWidth={2} />
                    </button>
                  )}
                  <button
                    type="button"
                    className="Chat-delete"
                    onClick={() => onDeleteMessage(msg.id)}
                    aria-label="Poista"
                    title="Poista"
                  >
                    <X className="Chat-action-icon" size={24} strokeWidth={2} />
                  </button>
                </div>
              ) : !msg.isCommandResponse && !isPulinaBot ? (
                <button
                  type="button"
                  className="Chat-reply"
                  onClick={() => onReply && onReply(msg)}
                  aria-label="Vastaa"
                  title="Vastaa"
                >
                  <Reply className="Chat-reply-icon" size={24} strokeWidth={2} />
                </button>
              ) : null}
            </div>
            </div>
          );
        }
        const { authorKey, authorName, messages: groupMsgs } = block;
        const groupAuthorLower = (authorName || '').trim().toLowerCase();
        const groupIsBot = groupAuthorLower === 'pulina' || groupAuthorLower === 'pulinabotti';
        const groupVisual = visualForAuthor(authorVisualCache, authorName);
        const gKey = authorKey;
        const gImgBroken = !!avatarImageBroken[gKey];
        return (
          <div key={`muted-${authorKey}-${groupMsgs[0]?.id}`} className="Chat-bubble-row">
            <ChatBubblePrefix
              authorKey={authorKey}
              authorName={authorName}
              isBot={groupIsBot}
              isOwn={false}
              muted
              muteCount={groupMsgs.length}
              onToggleMute={onToggleMuteAuthor}
              visual={groupVisual}
              imageBroken={gImgBroken}
              onImageError={() => setAvatarImageBroken((p) => ({ ...p, [gKey]: true }))}
            />
            <div className="Chat-bubble Chat-bubble-other Chat-bubble-muted">
              <div className="Chat-bubble-author-row">
                {!groupIsBot && authorName && authorName.trim().toLowerCase() !== currentUserName ? (
                  <button
                    type="button"
                    className="Chat-author-link"
                    onClick={() => openPublicProfileFromChat(authorName)}
                  >
                    <span className="Chat-bubble-author">{authorName}</span>
                  </button>
                ) : (
                  <span className="Chat-bubble-author">{authorName}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
      <div ref={messagesEndRef} aria-hidden="true" />
      {publicProfileModalName &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="Chat-public-profile-modal-backdrop"
            role="presentation"
            onClick={closePublicProfileModal}
          >
            <div
              className="Chat-public-profile-modal-panel"
              role="dialog"
              aria-modal="true"
              aria-label="Julkinen profiili"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="Chat-public-profile-modal-close"
                onClick={closePublicProfileModal}
                aria-label="Sulje"
              >
                <X size={22} strokeWidth={2} />
              </button>
              <div className="Chat-public-profile-modal-body">
                <PublicProfileView displayName={publicProfileModalName} />
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default ChatMessages;
